import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import Layout from '../components/Layout';
import './UserDashboard.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const UserDashboard = () => {
  const navigate = useNavigate();
  const { user, logout, sessionToken } = useAuth();
  const [albums, setAlbums] = useState([]);
  const [seasonalAlbums, setSeasonalAlbums] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('albums'); // 'albums' or 'seasonal'

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    await Promise.all([loadAlbums(), loadSeasonalAlbums()]);
    setLoading(false);
  };

  const loadAlbums = async () => {
    try {
      const response = await axios.get(`${API}/albums`, {
        headers: {
          Authorization: `Bearer ${sessionToken}`
        }
      });
      if (response.data.success) {
        setAlbums(response.data.albums);
      }
    } catch (error) {
      console.error('Error loading albums:', error);
    }
  };

  const loadSeasonalAlbums = async () => {
    try {
      const response = await axios.get(`${API}/seasonal-albums`, {
        headers: {
          Authorization: `Bearer ${sessionToken}`
        }
      });
      if (response.data.success) {
        setSeasonalAlbums(response.data.seasonal_albums);
      }
    } catch (error) {
      console.error('Error loading seasonal albums:', error);
    }
  };

  const handleDeleteAlbum = async (albumId) => {
    if (!window.confirm('Are you sure you want to delete this album?')) {
      return;
    }

    try {
      const response = await axios.delete(`${API}/albums/${albumId}`, {
        headers: {
          Authorization: `Bearer ${sessionToken}`
        }
      });

      if (response.data.success) {
        await loadAlbums();
      }
    } catch (error) {
      console.error('Error deleting album:', error);
      alert('Failed to delete album');
    }
  };

  const downloadImage = (imageData, fileName) => {
    const link = document.createElement('a');
    link.href = imageData.startsWith('data:') ? imageData : `data:image/jpeg;base64,${imageData}`;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Layout>
      <div className="user-dashboard">
        <div className="dashboard-container">
        {/* Key Metrics - Top Priority */}
        <div className="key-metrics">
          <div className="metric-card primary">
            <div className="metric-value">{albums.length}</div>
            <div className="metric-label">Model Albums</div>
          </div>
          <div className="metric-card">
            <div className="metric-value">{seasonalAlbums.length}</div>
            <div className="metric-label">Collections</div>
          </div>
          <div className="metric-card highlight">
            <div className="metric-value">{user?.image_credits || 0}</div>
            <div className="metric-label">Credits Left</div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="tab-navigation">
          <button
            className={`tab-btn ${activeTab === 'albums' ? 'active' : ''}`}
            onClick={() => setActiveTab('albums')}
          >
            📸 My Albums ({albums.length})
          </button>
          <button
            className={`tab-btn ${activeTab === 'seasonal' ? 'active' : ''}`}
            onClick={() => setActiveTab('seasonal')}
          >
            🌟 Collections ({seasonalAlbums.length})
          </button>
        </div>

        {loading ? (
          <div className="loading">Loading your content...</div>
        ) : (
          <>
            {/* Albums Tab */}
            {activeTab === 'albums' && (
              <div className="tab-content">
                {albums.length === 0 ? (
                  <div className="empty-state">
                    <div className="empty-icon">📸</div>
                    <h3>No Albums Yet</h3>
                    <p>Create your first album to store photos for personalized try-ons</p>
                    <button className="action-btn" onClick={() => navigate('/albums')}>
                      Create First Album
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="section-actions">
                      <button className="action-btn" onClick={() => navigate('/albums')}>
                        + Create New Album
                      </button>
                    </div>
                    <div className="content-grid">
                      {albums.map((album) => (
                        <div key={album.id} className="dashboard-card album-card">
                          <div className="card-header">
                            <h3>{album.name}</h3>
                            <span className={`badge ${album.category}`}>
                              {album.category === 'me' && '👤'}
                              {album.category === 'partner' && '👫'}
                              {album.category === 'family' && '👨‍👩‍👧‍👦'}
                              {album.category === 'custom' && '📁'}
                            </span>
                          </div>
                          
                          {album.photos && album.photos.length > 0 ? (
                            <div className="card-preview">
                              <div className="photo-mini-grid">
                                {album.photos.slice(0, 3).map((photo, idx) => (
                                  <img
                                    key={idx}
                                    src={photo.image_base64.startsWith('data:') ? photo.image_base64 : `data:image/jpeg;base64,${photo.image_base64}`}
                                    alt={photo.position}
                                  />
                                ))}
                              </div>
                            </div>
                          ) : (
                            <div className="card-empty">No photos uploaded</div>
                          )}

                          <div className="card-status">
                            <span className={`status ${album.is_complete ? 'complete' : 'incomplete'}`}>
                              {album.is_complete ? '✓ Complete' : `${album.photo_count}/3 photos`}
                            </span>
                          </div>

                          <div className="card-actions">
                            <button className="btn-primary" onClick={() => navigate('/albums')}>
                              Manage
                            </button>
                            <button className="btn-danger" onClick={() => handleDeleteAlbum(album.id)}>
                              Delete
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Seasonal Collections Tab */}
            {activeTab === 'seasonal' && (
              <div className="tab-content">
                {seasonalAlbums.length === 0 ? (
                  <div className="empty-state">
                    <div className="empty-icon">🌟</div>
                    <h3>No Collections Yet</h3>
                    <p>Generate your first collection - it's FREE!</p>
                    <button className="action-btn" onClick={() => navigate('/tryon')}>
                      Explore Templates
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="section-actions">
                      <button className="action-btn" onClick={() => navigate('/tryon')}>
                        Generate New Collection
                      </button>
                    </div>
                    <div className="content-grid">
                      {seasonalAlbums.map((collection) => (
                        <div key={collection.id} className="dashboard-card seasonal-card">
                          <div className="card-header">
                            <h3>{collection.template_name}</h3>
                            <span className="date">
                              {new Date(collection.created_at).toLocaleDateString()}
                            </span>
                          </div>

                          <div className="card-preview">
                            <div className="seasonal-preview-grid">
                              {collection.generated_images.slice(0, 4).map((img, idx) => (
                                <div key={idx} className="preview-item">
                                  <img
                                    src={img.result_image.startsWith('data:') ? img.result_image : `data:image/jpeg;base64,${img.result_image}`}
                                    alt={img.garment_name}
                                  />
                                </div>
                              ))}
                            </div>
                          </div>

                          <div className="card-info">
                            <span className="info-item">📷 {collection.generated_images.length} images</span>
                            <span className={`credit-tag ${collection.credits_used === 0 ? 'free' : ''}`}>
                              {collection.credits_used === 0 ? '🎉 FREE' : `💎 ${collection.credits_used}`}
                            </span>
                          </div>

                          <div className="card-actions">
                            <button className="btn-primary" onClick={() => navigate('/seasonal-gallery')}>
                              View Details
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}
          </>
        )}
        </div>
      </div>
    </Layout>
  );
};

export default UserDashboard;
